import { refs } from '../core/refs.js';
import { Modal } from '../core/modal.js';
import { panelSystem } from '../ui/panels.js';
import { isMac } from '../core/os.js';

/**
 * Front-my-End — Dropdown Menu Feature
 */

export function registerMenuFeature() {
  const dropdown = refs.quickMenuDropdown;
  if (!dropdown) return;

  const toggle = (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('active');
  };

  if (refs.menuBtn) refs.menuBtn.addEventListener('click', toggle);

  // Outside-click dismissal
  document.addEventListener('click', (e) => {
    if (dropdown.contains(e.target)) return;
    if (refs.menuBtn && (e.target === refs.menuBtn || refs.menuBtn.contains(e.target))) return;
    dropdown.classList.remove('active');
  });

  // Delegate menu operations to primary button handlers
  const wire = (el, handler) => {
    if (el) {
      el.addEventListener('click', () => {
        handler();
        closeMenu();
      });
    }
  };

  wire(refs.menuRunBtn, () => refs.runBtn.click());
  wire(refs.menuExportBtn, () => refs.exportBtn.click());
  wire(refs.menuShareBtn, () => refs.shareBtn.click());
  wire(refs.menuResetBtn, () => refs.resetBtn.click());
  wire(refs.menuImportBtn, () => refs.importBtn.click());
  wire(refs.menuMinimizeAllBtn, () => panelSystem.minimizeAll());
  wire(refs.menuRestoreAllBtn, () => panelSystem.restoreAll());

  // Privacy Policy and Terms of Service Modals
  wire(refs.menuPrivacyBtn, () => {
    Modal.showMessage('Privacy Policy', createLegalDoc([
      {
        title: '1. 100% Local-First Architecture',
        text: 'Front-my-End is engineered as a local-first browser playground. Code editing, WebAssembly syntax analysis, formatting, diagnostics, and preview rendering run entirely inside your browser on your device. Your workspace code and snippets are never uploaded, analyzed, or sent to any remote server during playground use.'
      },
      {
        title: '2. Cookies & Browser Storage',
        text: 'Front-my-End does not set cookies, and our analytics writes nothing to your device: its anonymous visitor ID lives in memory only and is gone when you reload. Browser localStorage is used strictly for core functionality: persisting your HTML, CSS, JavaScript, scratchpad notes, theme, Live Preview setting, layout preferences, and preview-safety state (the JavaScript you last ran and whether the last preview finished) across page reloads. This data stays on your device. You can clear it at any time via the Reset Playground menu action or through your browser settings.'
      },
      {
        title: '3. Privacy-Preserving Diagnostics & Analytics',
        text: 'Front-my-End uses PostHog, hosted in its EU region (eu.i.posthog.com), for stability monitoring, crash diagnostics, and high-level product analytics. We collect page views, interactions with the app controls (with page text masked), app error reports, a few named events such as Live Preview being toggled, and session replays with all text masked. Your browser also sends its type, operating system, screen size, language, and referring page, and PostHog uses your IP address to derive an approximate location. The code editors, find bar, scratchpad, console, preview, and share dialog are fully blocked from replay and event data is filtered, so your code, markup, scripts, and notes are NEVER recorded or transmitted. No cookie or stored identifier links your visits, and we do not fingerprint devices. If your browser sends Do Not Track, analytics does not start. We rely on our legitimate interest in keeping the playground stable.'
      },
      {
        title: '4. Hosted Share Links (Online Services)',
        text: 'When you create a hosted share, the selected HTML, CSS, JavaScript, preview documents, title, theme choices, and share metadata are sent to the share service and stored temporarily so the link can load. Hosted shares are unlisted public records accessible to anyone with the link. Creating a share may run Cloudflare Turnstile, a bot check that Cloudflare processes under its own privacy policy.'
      },
      {
        title: '5. Retention & Abuse Prevention',
        text: 'Hosted shares are temporary and expire automatically. Share records may be removed without notice to prevent abuse, mitigate security risks, or comply with legal requirements.'
      },
      {
        title: '6. Contact & Inquiries',
        text: 'For questions, feedback, or data requests regarding privacy, contact privacy@frontmyend.com.'
      }
    ]));
  });

  wire(refs.menuTermsBtn, () => {
    Modal.showMessage('Terms of Service', createLegalDoc([
      {
        title: '1. Acceptance of Terms',
        text: 'By using Front-my-End, you agree to these Terms of Service governing local editing, previewing, exporting, and optional hosted sharing.'
      },
      {
        title: '2. Permitted Use & Source-Available License',
        text: 'Front-my-End is provided under a Source-Available / Commercial license. You are granted permission to inspect, run, and modify the software locally for personal, educational, research, and non-commercial development use. Providing the software or derivative works as a commercial web playground, hosted SaaS, or competing fiddle platform is strictly prohibited without prior written commercial licensing.'
      },
      {
        title: '3. Sandbox & User Responsibility',
        text: 'You are solely responsible for the code, files, scripts, and assets you execute, import, export, or share. You agree not to run or share malicious, harmful, abusive, unlawful, or rights-infringing content. The isolated preview sandbox provides execution isolation, but you assume all responsibility for user-authored code running in your browser.'
      },
      {
        title: '4. Hosted Shares & Public Access',
        text: 'Hosted shares are public to anyone with the link, may expire, and may be disabled or removed for safety, abuse prevention, legal compliance, or service maintenance.'
      },
      {
        title: '5. Warranty Disclaimer ("AS IS")',
        text: 'Front-my-End is provided "AS IS" and "AS AVAILABLE", without warranties of any kind, whether express, implied, or statutory, including warranties of merchantability, fitness for a particular purpose, non-infringement, or data preservation. We do not warrant that operation will be error-free or uninterrupted.'
      },
      {
        title: '6. Limitation of Liability',
        text: 'To the fullest extent permitted by applicable law, the authors, maintainers, and contributors are not liable for any direct, indirect, incidental, special, or consequential damages, including loss of data, loss of work, browser tab crashes, or hardware issues arising out of or related to your use of the service.'
      },
      {
        title: '7. Takedown & Abuse Reports',
        text: 'To report copyright infringement or abusive shared links, please contact abuse@frontmyend.com.'
      }
    ]));
  });

  wire(refs.menuDocsBtn, () => {
    Modal.showMessage('Quick Guide', createLegalDoc([
      {
        title: '1. Start Editing',
        text: 'Write markup in HTML, styles in CSS, and behavior in JavaScript. Press Run Workspace to rebuild the isolated preview, or turn on Live to see CSS changes instantly and HTML changes after a short pause. JavaScript only runs when you press Run, so a half-typed loop can never freeze the preview.'
      },
      {
        title: '2. Templates and Themes',
        text: 'Open Quick Controls and use Workspace Settings to load a starter template, change the visual theme, or reset the workspace view mode. Templates replace the editor contents, so export or copy anything you want to keep first.'
      },
      {
        title: '3. Panel Sizes and Layout',
        text: 'On desktop, drag a panel header to move it and drag any panel edge to resize it. Use the minus button to minimize a panel, the focus button to expand one panel above the workspace, double-click a header to reset that panel, or use Quick Controls to minimize or restore all panels.'
      },
      {
        title: '4. Desktop and Mobile Views',
        text: 'Use the View selector in the top bar or Quick Controls to choose Adaptive, Desktop Layout, or Mobile Layout. Adaptive follows the window width. Desktop shows movable panels. Mobile uses bottom tabs for HTML, CSS, JS, Preview, and Console, and also supports horizontal swipes between those panels.'
      },
      {
        title: '5. Preview, Console, and Inspect',
        text: 'The preview runs your code in a sandboxed frame. Open the Console panel to read runtime messages and playground notices. Use Inspect to click a preview element and jump back to the related source context; press Escape to leave inspect mode or dismiss active menus.'
      },
      {
        title: '6. Import',
        text: 'Use Import File from the top bar or Quick Controls, or drag files onto the workspace. Supported files are .html, .htm, .css, and .js up to 512 KB each. Imported HTML is split into body markup, inline styles, and inline scripts when possible.'
      },
      {
        title: '7. Export and Share',
        text: 'Use Export Workspace to download either one standalone HTML file or a ZIP containing index.html, styles.css, and script.js. Use Share Preview to create a temporary hosted link when online sharing is enabled; anyone with that link can open the shared preview.'
      },
      {
        title: '8. Keyboard Shortcuts',
        text: (isMac()
          ? '• ⌘Enter: Run Workspace / Rebuild Preview\n• ⌘S: Save workspace to local storage\n• ⌘E: Open Export Workspace dialog\n• ⌘I: Toggle Preview Element Inspector\n• ⌘F: Open Find / Search in active editor\n• ⇧⌥F: Format / Prettify active editor code\n• ⌥L or ⇧⌘R: Toggle Live Preview (CSS + HTML)\n• ⌘1 to ⌘5: Switch panel (1:HTML, 2:CSS, 3:JS, 4:Preview, 5:Console)\n• ⇧⌘L: Clear Console output logs\n• Escape: Dismiss search bar, menus, inspector, or focus mode\n• Tab / ⇧Tab: Indent / Outdent selected lines\n• ⌘Z / ⇧⌘Z: Undo / Redo edits\n• Auto-pairs brackets and quotes (), [], {}, "", \'\', `` with skip and pair backspace.\n(Shortcuts adapt automatically to your operating system via 100% local in-browser detection; zero telemetry is collected).'
          : '• Ctrl+Enter: Run Workspace / Rebuild Preview\n• Ctrl+S: Save workspace to local storage\n• Ctrl+E: Open Export Workspace dialog\n• Ctrl+I: Toggle Preview Element Inspector\n• Ctrl+F: Open Find / Search in active editor\n• Shift+Alt+F: Format / Prettify active editor code\n• Alt+L or Ctrl+Shift+R: Toggle Live Preview (CSS + HTML)\n• Ctrl+1 to Ctrl+5: Switch panel (1:HTML, 2:CSS, 3:JS, 4:Preview, 5:Console)\n• Ctrl+Shift+L: Clear Console output logs\n• Escape: Dismiss search bar, menus, inspector, or focus mode\n• Tab / Shift+Tab: Indent / Outdent selected lines\n• Ctrl+Z / Ctrl+Y (or Ctrl+Shift+Z): Undo / Redo edits\n• Auto-pairs brackets and quotes (), [], {}, "", \'\', `` with skip and pair backspace.\n(Shortcuts adapt automatically to your operating system via 100% local in-browser detection; zero telemetry is collected).')
          + '\n(Core: Cmd/Ctrl+Enter runs the workspace, Cmd/Ctrl+S saves locally, Cmd/Ctrl+E exports).'
      },
      {
        title: '9. Scratch Pad and Local State',
        text: 'Open Scratch Pad from Quick Controls for notes or temporary snippets. Scratch Pad text is saved in your browser and is never compiled into the preview. Editor contents, theme, view mode, and layout preferences are also stored locally in this browser.'
      }
    ]));
  });

  wire(refs.menuAboutBtn, () => {
    Modal.showMessage('About Front-my-End', createLegalDoc([
      {
        title: '1. What It Is',
        text: 'Front-my-End is a lightweight HTML, CSS, and JavaScript playground built with a Rust/WASM compute core and a package-light JavaScript interface.'
      },
      {
        title: '2. Architecture',
        text: 'Rust owns formatter, scanner, tokenizer, and validator compute. JavaScript owns the browser UI, template loading, preview document assembly, and share flow.'
      },
      {
        title: '3. Preview Safety',
        text: 'User code runs in an isolated preview sandbox. Production sharing uses hosted preview documents and the dedicated preview origin instead of running inside the app shell.'
      },
      {
        title: '4. Design Goal',
        text: 'The project is designed for fast front-end experimentation across desktop and mobile layouts while keeping the codebase small, explicit, and maintainable.'
      }
    ]));
  });
}

function createLegalDoc(sections) {
  const wrapper = document.createElement('div');
  wrapper.className = 'legal-doc-content';

  sections.forEach((section) => {
    const paragraph = document.createElement('p');
    const strong = document.createElement('strong');
    strong.textContent = section.title;
    paragraph.appendChild(strong);
    paragraph.appendChild(document.createElement('br'));
    const lines = String(section.text || '').split('\n');
    lines.forEach((line, idx) => {
      if (idx > 0) paragraph.appendChild(document.createElement('br'));
      paragraph.append(line);
    });
    wrapper.appendChild(paragraph);
  });

  return wrapper;
}

export function closeMenu() {
  if (refs.quickMenuDropdown) {
    refs.quickMenuDropdown.classList.remove('active');
  }
}
